import React, { createRef, memo, MutableRefObject, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import DotLoader from '@svg/dot_loader';

import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, State } from '@store/index';
import { Message } from './ChatMessage/Message';
import { Alert, Divider, IconButton, InputBase, Stack } from '@mui/material';
import { Send, OpenInFull } from '@mui/icons-material';
import { Select } from '@common/Select';
import { isNodeError, runWorkflow, WorkflowChat } from '@store/async/run';
import { useParams } from 'react-router-dom';
import { getRequestChatParameters } from '@common/utils/execution';
import { useTranslate } from '@common/hooks/useTranslate';
import { useSnackbar } from 'notistack';
import { ChatExpandModal } from '@components/Modals/ChatExpandModal/ChatExpandModal';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  // total - topnav - sidebar header - first tab row - second tab row;
  height: calc(100vh - 60px - 61px - 60px);
`;

const MessageContainer = styled.div<{ multipleVariables?: boolean; sendDisabled?: boolean }>`
  flex: 1;
  width: 100%;
  word-break: break-word;
  overflow-y: auto;
  margin-bottom: ${({ sendDisabled, multipleVariables }) =>
    sendDisabled ? (multipleVariables ? '45px' : '0px') : multipleVariables ? '145px' : '125px'};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  position: absolute;
  bottom: 0;
  background: ${({ theme }) => `linear-gradient(0deg, ${theme.background.standard} 80%, transparent 100%)`};
`;

const Action = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  width: 22px;
`;

const ChatInput = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 80%;
  min-width: 80%;
  background-color: ${({ theme }) => theme.background.standard};
  border: 1px solid ${({ theme }) => theme.line.light};
  border-radius: 5px;
  padding: 0.35rem 0.5rem;
  padding-right: 1rem;
  margin-bottom: 0rem;
  margin-top: 1rem;

  box-shadow: 0px 6px 15px -1px rgba(1, 1, 1, 0.2);

  svg {
    color: ${({ theme }) => theme.text.standard};
  }
`;

const VariableInput = styled.div`
  display: flex;
  margin-bottom: 2rem;
  max-width: 80%;
  min-width: 80%;
`;

const extractVariables = (chats: WorkflowChat[]): string[] => {
  const variablePattern = /\{\{\s*(.*?)\s*\}\}/g;
  const variables = new Set<string>();
  let match;

  for (const chat of chats) {
    const template = chat.template;

    while ((match = variablePattern.exec(template)) !== null) {
      variables.add(match[1].trim());
    }
  }

  return Array.from(variables);
};

interface Props {
  abortControllerRef: MutableRefObject<AbortController | null>;
  start: () => void;
  pause: () => void;
  reset: () => void;
}

export const ChatbotWindow = memo(({ abortControllerRef, start, pause, reset }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const bottomRef = createRef<HTMLDivElement>();

  const { service_id: workspace_id, workflow_id } = useParams();
  const { translate } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  const [alt, setAlt] = useState(false);
  const [input, setInput] = useState<string>('');
  const [chatVariable, setChatVariable] = useState<string>('');
  const [expandModalOpen, setExpandModalOpen] = useState<string | null>(null);

  const dataPrefixes = useSelector((state: State) => state.tabs.active.flow?.data_prefixes) || {};
  const executionParameters = useSelector((state: State) => state.tabs.active.run.parameters);
  const executionChat = useSelector((state: State) => state.tabs.active.run.chat);
  const executionRunning = useSelector((state: State) => state.tabs.active.run.running);
  const executionError = useSelector((state: State) => state.tabs.active.run.error);
  const executionChatNodes = useSelector((state: State) => state.tabs.active.run.chatNodes);
  const executionMessages = useSelector((state: State) => state.tabs.active.run.messages);

  const loadingUserMessages = executionMessages.filter(
    (message) => message.type === 'user' && (message.state === 'loading' || message.state === 'streaming')
  );

  const isChatWorkflow = executionChat?.some((item) => item?.query);
  const traceVariables = executionChat ? extractVariables(executionChat) : [];
  const isSendDisabled =
    executionChatNodes?.some((item) => item?.state?.message_array?.enabled) || traceVariables.length === 0;

  const tools = executionMessages?.map((message) => message.events.functions).flat();
  const hasTools = tools.length > 0;

  const nodesWithoutSessionId = executionChat
    .filter((chatNode) => !chatNode.session_id)
    .map((chatNode) => chatNode.node_id)
    .map((item) => `node_${item}`);

  const singleChat = executionChat.length === 1;
  const hasNoSessionId = nodesWithoutSessionId.length > 0;

  const sendMessage = async () => {
    reset();

    if (!executionChat || executionRunning || !executionParameters) return;

    setInput('');
    start();

    abortControllerRef.current = new AbortController();

    const translation = translate(workflow_id || '');
    const parameters = getRequestChatParameters(executionParameters, input, chatVariable, dataPrefixes);

    const response = await dispatch(
      runWorkflow({
        workspace_id,
        workflow_id,
        parameters,
        translation,
        abortController: abortControllerRef.current,
      })
    );

    pause();

    if (isNodeError(response.payload)) {
      enqueueSnackbar(response.payload.error.execution.reason, { variant: 'error', autoHideDuration: 8000 });
      enqueueSnackbar('Workflow Execution Failed', { variant: 'error', autoHideDuration: 3000 });
    }
  };

  useEffect(() => {
    if (traceVariables.length && chatVariable === '') setChatVariable(traceVariables[0]);
  }, [traceVariables]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [loadingUserMessages]);

  if (executionError) {
    return (
      <Stack padding='16px 20px'>
        <Alert
          severity='error'
          sx={{ whiteSpace: 'pre-wrap' }}
        >{`A node in this workflow failed to execute.\n\n"${executionError}"`}</Alert>
      </Stack>
    );
  }

  if (!isChatWorkflow) {
    return (
      <Stack padding='16px 20px'>
        <Alert severity='warning'>This workflow is not eligible for workflow chat.</Alert>
      </Stack>
    );
  }

  return (
    <Container>
      {!expandModalOpen && (
        <>
          {hasNoSessionId && !singleChat && (
            <Stack padding='16px 20px 0 20px'>
              <Alert severity='info'>
                A completion node in this workflow does not have a session ID defined. The LLM will not be able to
                recall previous answers in this chat window without it ({nodesWithoutSessionId.join(', ')}).
              </Alert>
            </Stack>
          )}
          {hasNoSessionId && singleChat && (
            <Stack padding='16px 20px 0 20px'>
              <Alert severity='info'>
                This completion does not have a session ID defined. The LLM will not be able to recall previous answers
                without it.
              </Alert>
            </Stack>
          )}
          {hasNoSessionId && <Divider sx={{ marginTop: '16px' }} />}
          <MessageContainer multipleVariables={traceVariables.length > 1} sendDisabled={isSendDisabled}>
            {executionMessages.map((message, index) => (
              <Message key={index} entry={message} setExpandModalOpen={setExpandModalOpen} />
            ))}
            <div ref={bottomRef} />
          </MessageContainer>
          {((isSendDisabled && tools.length > 0) || !isSendDisabled) && (
            <Footer>
              {!isSendDisabled && (
                <ChatInput>
                  <InputBase
                    inputRef={inputRef}
                    maxRows={4}
                    multiline
                    onKeyDown={(e) => {
                      if (e.key === 'Control' || e.key === 'Shift') return setAlt(true);
                      if (e.key === 'Enter' && alt) return;
                      if (e.key !== 'Enter') return;
                      if (input.trim() === '') return;

                      e.preventDefault();
                      e.stopPropagation();

                      sendMessage();
                    }}
                    onKeyUp={(e) => {
                      if (e.key === 'Control' || e.key === 'Shift') return setAlt(false);
                    }}
                    sx={{ ml: 1, flex: 1 }}
                    placeholder='Send a message...'
                    value={input}
                    onChange={(e) => setInput(e?.target?.value)}
                  />
                  <Stack direction='row' spacing={'2px'}>
                    {executionRunning ? (
                      <IconButton disabled>
                        <Action>
                          <DotLoader height={6} />
                        </Action>
                      </IconButton>
                    ) : (
                      <IconButton onClick={sendMessage}>
                        <Action>
                          <Send sx={{ fontSize: '1.4ch' }} />
                        </Action>
                      </IconButton>
                    )}
                    <IconButton onClick={() => setExpandModalOpen('noTool')} title='Expand chat'>
                      <Action>
                        <OpenInFull sx={{ fontSize: '1.4ch' }} />
                      </Action>
                    </IconButton>
                  </Stack>
                </ChatInput>
              )}
              <VariableInput>
                {traceVariables.length > 1 && (
                  <Select
                    value={chatVariable}
                    setValue={setChatVariable}
                    options={traceVariables.map((item) => ({
                      label: item.replace('http.request.', ''),
                      value: item,
                    }))}
                  />
                )}
              </VariableInput>
            </Footer>
          )}
        </>
      )}
      <ChatExpandModal
        openTool={expandModalOpen}
        setExpandModalOpen={setExpandModalOpen}
        messages={executionMessages}
        input={input}
        setInput={setInput}
        sendMessage={sendMessage}
        executionRunning={executionRunning}
        chatVariable={chatVariable}
        setChatVariable={setChatVariable}
        traceVariables={traceVariables}
        hasTools={hasTools}
      />
    </Container>
  );
});

ChatbotWindow.displayName = 'ChatWindow';
